import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorNotification, showSuccessNotification } from '@/components/common/utils/notify';

export interface BranchConfigDTO {
    branch: string;
    brFname?: string;
    brName?: string;
    brsesStat?: string;
    brsesDate?: string;
    address?: string;
    telephone?: string;
    mobile?: string;
    email?: string;
    website?: string;
    logo?: string;
    contactPerson?: string;
    regNo?: string;
    // Extension fields
    region?: string;
    district?: string;
    ward?: string;
    postalAddress?: string;
    managerName?: string;
    openingHours?: string;
    servicesOffered?: string;
    gpsCoordinates?: string;
}

const BASE = '/api/admin/config/branch';

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || res.statusText);
    }
    return res.json();
}

export const useBranchConfig = (branchCode: string) =>
    useQuery<BranchConfigDTO>({
        queryKey: ['branch-config', branchCode],
        queryFn: () => apiFetch(`${BASE}/${branchCode}`),
        enabled: !!branchCode,
    });

export const useUpdateBranchConfig = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: BranchConfigDTO) =>
            apiFetch<BranchConfigDTO>(`${BASE}/${dto.branch}`, {
                method: 'PUT',
                body: JSON.stringify(dto),
            }),
        onSuccess: (_, vars) => {
            qc.invalidateQueries({ queryKey: ['branch-config', vars.branch] });
            showSuccessNotification('Branch configuration saved');
        },
        onError: (e: Error) => showErrorNotification(e.message),
    });
};
